import { FormControl, OutlinedInput, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ButtonGroup } from '@components/ButtonGroup';
import CustomRangeDialog from '@components/DateRange';
import InputLabel from '@components/InputLabel';
import type { TabValue } from '@components/ViewTab/ViewTab';

import { useHealthChart } from '@hooks/useHealthChart';
import { useIsMobile } from '@hooks/useIsMobile';

import {
  DAYJS_MAP,
  Interval,
  INTERVALS,
  MOBILE_INTERVAL_RANGES,
  WEB_INTERVAL_RANGES,
} from '@models/graph';

const messages = defineMessages({
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
  date: {
    id: 'healthTracker.filters.date',
    defaultMessage: 'Date',
  },
  moreDates: {
    id: 'healthTracker.filters.moreDates',
    defaultMessage: 'More Dates',
  },
  custom: {
    id: 'healthTracker.filters.custom',
    defaultMessage: 'Custom',
  },
  lastHours: {
    id: 'healthTracker.filters.lastHours',
    defaultMessage:
      'Last {hourCount, plural, one {hour} other {{hourCount, number} hours}}',
  },
  lastDays: {
    id: 'healthTracker.filters.lastDays',
    defaultMessage:
      'Last {dayCount, plural, one {day} other {{dayCount, number} days}}',
  },
  lastWeeks: {
    id: 'healthTracker.filters.lastWeeks',
    defaultMessage:
      'Last {weekCount, plural, one {week} other {{weekCount, number} weeks}}',
  },
  lastMonths: {
    id: 'healthTracker.filters.lastMonths',
    defaultMessage:
      'Last {monthCount, plural, one {month} other {{monthCount, number} months}}',
  },
  lastYears: {
    id: 'healthTracker.filters.lastYears',
    defaultMessage:
      'Last {yearCount, plural, one {year} other {{yearCount, number} years}}',
  },
  hours: {
    id: 'healthTracker.filters.hours',
    defaultMessage: 'Hours',
  },
  days: {
    id: 'healthTracker.filters.days',
    defaultMessage: 'Days',
  },
  weeks: {
    id: 'healthTracker.filters.weeks',
    defaultMessage: 'Weeks',
  },
  months: {
    id: 'healthTracker.filters.months',
    defaultMessage: 'Months',
  },
  years: {
    id: 'healthTracker.filters.years',
    defaultMessage: 'Years',
  },
  graph: {
    id: 'healthTracker.detail.graph',
    defaultMessage: 'Graph',
  },
  table: {
    id: 'healthTracker.detail.table',
    defaultMessage: 'Table',
  },
});

export default function DateSelector() {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const { interval, range } = useHealthChart();
  const { handleDateChange: changeDateFilters, maxDate } = useHealthChart();

  const [customOpen, setCustomOpen] = React.useState<boolean>(false);

  const value = `${interval}-${range || 1}`;

  const tabs: TabValue<Interval>[] = React.useMemo(
    () => [
      // {
      //   id: Interval.Hour,
      //   value: formatMessage(messages.hours),
      // },
      {
        id: Interval.Day,
        value: formatMessage(messages.days),
      },
      {
        id: Interval.Week,
        value: formatMessage(messages.weeks),
      },
      {
        id: Interval.Month,
        value: formatMessage(messages.months),
      },
      {
        id: Interval.Year,
        value: formatMessage(messages.years),
      },
      {
        id: Interval.Custom,
        value: formatMessage(messages.custom),
      },
    ],
    [formatMessage]
  );

  const getFormattedMessage = (currentInterval: Interval, value: number) => {
    switch (currentInterval) {
      case Interval.Hour:
        return formatMessage(messages.lastHours, {
          hourCount: value,
        });
      case Interval.Day:
        return formatMessage(messages.lastDays, {
          dayCount: value,
        });
      case Interval.Week:
        return formatMessage(messages.lastWeeks, {
          weekCount: value,
        });
      case Interval.Month:
        return formatMessage(messages.lastMonths, {
          monthCount: value,
        });
      case Interval.Year:
        return formatMessage(messages.lastYears, {
          yearCount: value,
        });
      default:
        break;
    }
  };

  const options = Array.from(
    isMobile ? MOBILE_INTERVAL_RANGES.entries() : WEB_INTERVAL_RANGES.entries()
  ).reduce<JSX.Element[]>((acc, curr) => {
    const [interval, rangeOptions] = curr;
    for (const val of rangeOptions) {
      const text = getFormattedMessage(interval, val);

      acc.push(
        <option
          key={`metric-date-${interval}-${val}`}
          value={`${interval}-${val}`}
        >
          {interval === Interval.Custom ? formatMessage(messages.custom) : text}
        </option>
      );
    }
    return acc;
  }, []);

  const handleIntervalChange = (value: number | string) => {
    // Keep maxDate; update minDate depending on selected interval
    const newInterval = value as Interval;
    if (newInterval === Interval.Custom) {
      handleOpenCustomDialog();
      return;
    }
    const min = dayjs(maxDate).subtract(1, DAYJS_MAP[newInterval]).toDate();
    const max = maxDate;
    changeDateFilters(INTERVALS[newInterval], min, max, 1);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { value } = event.target;

    const [newInterval, newRange] = value.split('-');

    const interval = newInterval as Interval;
    const range = Number.isNaN(newRange) ? null : parseInt(newRange);

    if (interval === Interval.Custom) {
      handleOpenCustomDialog();
      return;
    }
    if (!range) {
      return;
    }
    const max = maxDate;
    const min = dayjs(maxDate).subtract(range, DAYJS_MAP[interval]).toDate();
    changeDateFilters(INTERVALS[interval], min, max, range);
  };

  const handleOpenCustomDialog = () => {
    setCustomOpen(true);
  };

  const handleCloseDialog = () => {
    setCustomOpen(false);
  };

  const handleApplyCustomRange = (start: Date, end: Date) => {
    changeDateFilters(INTERVALS[Interval.Custom], start, end);
    handleCloseDialog();
  };

  const { buttons, selectedButtons } = React.useMemo(() => {
    const buttons = tabs.map(({ id, value }) => ({
      name: value,
      value: id,
    }));
    const selectedButtons = buttons.map(
      ({ value }) => value === interval && range === 1
    );

    return { buttons, selectedButtons };
  }, [interval, range, tabs]);

  return (
    <>
      {!isMobile && (
        <ButtonGroup
          buttons={buttons}
          selected={selectedButtons}
          onClick={handleIntervalChange}
        />
      )}
      <FormControl size="small">
        <InputLabel
          inputLabelProps={{ shrink: true }}
          label={formatMessage(isMobile ? messages.date : messages.moreDates)}
          style={{
            color: 'text.secondary',
          }}
        />
        <Select
          autoWidth
          native
          onChange={handleSelectChange}
          input={
            <OutlinedInput
              size="small"
              sx={{
                minHeight: 30,
                boxShadow: (theme) => theme.shadows[1],
                height: 35,
                fontSize: '14px',
              }}
            />
          }
          value={value}
        >
          <option value="">{formatMessage(messages.select)}</option>
          {options.map((option) => option)}
        </Select>
      </FormControl>
      <CustomRangeDialog
        onCancel={handleCloseDialog}
        onApply={handleApplyCustomRange}
        open={customOpen}
      />
    </>
  );
}
